import { SDK_SCRIPT_ID } from "./constants";
import type { SensorsSDK } from "./types";

let sdkLoadPromise: Promise<SensorsSDK> | null = null;

export function loadSensorsSDK(sdkUrl: string): Promise<SensorsSDK> {
  const mountedSDK = getMountedSensorsSDK();

  if (mountedSDK) {
    return Promise.resolve(mountedSDK);
  }

  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }

  sdkLoadPromise = waitForBody().then((body) => {
    return new Promise<SensorsSDK>((resolve, reject) => {
      const existingScript = findSensorsScript(sdkUrl);
      const script = existingScript || document.createElement("script");

      script.id = script.id || SDK_SCRIPT_ID;
      script.type = "text/javascript";
      script.async = true;
      script.charset = "UTF-8";

      if (existingScript?.getAttribute("data-loaded") === "true") {
        const sensors = getMountedSensorsSDK();

        if (sensors) {
          resolve(sensors);
          return;
        }
      }

      script.addEventListener("load", () => {
        const sensors = getMountedSensorsSDK();

        if (sensors) {
          script.setAttribute("data-loaded", "true");
          resolve(sensors);
        } else {
          reject(new Error("Sensors SDK loaded, but window.sensorsDataAnalytic201505 is missing."));
        }
      });

      script.addEventListener("error", () => {
        reject(new Error(`Sensors SDK failed to load: ${sdkUrl}`));
      });

      if (!existingScript) {
        script.src = sdkUrl;
        body.appendChild(script);
      }
    });
  }).catch((error) => {
    sdkLoadPromise = null;
    throw error;
  });

  return sdkLoadPromise;
}

export function getMountedSensorsSDK(): SensorsSDK | null {
  if (typeof window === "undefined") {
    return null;
  }

  return window.sensorsDataAnalytic201505 || window.SensorsData || null;
}

function waitForBody(): Promise<HTMLElement> {
  if (typeof document === "undefined") {
    return Promise.reject(new Error("Document is not available."));
  }

  if (document.body) {
    return Promise.resolve(document.body);
  }

  return new Promise((resolve, reject) => {
    document.addEventListener("DOMContentLoaded", () => {
      if (document.body) {
        resolve(document.body);
      } else {
        reject(new Error("Document body is missing after DOMContentLoaded."));
      }
    }, { once: true });
  });
}

function findSensorsScript(sdkUrl: string): HTMLScriptElement | null {
  if (typeof document === "undefined") {
    return null;
  }

  const scriptById = document.getElementById(SDK_SCRIPT_ID) as HTMLScriptElement | null;

  if (scriptById) {
    return scriptById;
  }

  const scripts = document.getElementsByTagName("script");

  for (let i = 0; i < scripts.length; i++) {
    const script = scripts[i];

    if (script.getAttribute("src") === sdkUrl || script.src === sdkUrl) {
      return script;
    }
  }

  return null;
}
